<template>
  <component :is="layout">
    <!-- branchVersion (RX-4) re-monta la vista activa al cambiar de sucursal sin
         recargar la página: las cargas onMounted se re-ejecutan con el contexto nuevo.
         fullPath fuerza un remount por cada URL distinta (p.ej. /tasks/5 -> /tasks/6),
         caso en el que Vue Router reutiliza la instancia y onMounted NO se dispararía. -->
    <router-view :key="`${branchVersion}-${route.fullPath}`" />
  </component>

  <!-- RX-5: indicador de modo offline + cola de reintentos + nueva versión SW -->
  <transition name="banner">
    <div
      v-if="bannerMessage || swUpdateAvailable"
      class="fixed bottom-0 inset-x-0 z-[60] px-4 py-3 border-t shadow-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs font-bold"
      :class="swUpdateAvailable ? 'bg-brand-600 text-white border-brand-700' : 'bg-slate-900 text-white border-slate-800'"
      role="status"
      aria-live="polite"
    >
      <div class="flex items-center gap-2.5 font-medium">
        <WifiOff v-if="bannerMessage && !swUpdateAvailable" class="w-4 h-4 shrink-0" />
        <RefreshCw v-else class="w-4 h-4 shrink-0 animate-spin" />
        <span>
          <template v-if="swUpdateAvailable">
            Nueva versión de NEXUS CREDIT disponible.
          </template>
          <template v-else>
            {{ bannerMessage }}
          </template>
        </span>
      </div>

      <div class="flex items-center gap-3">
        <button
          v-if="retryQueue.length > 0"
          @click="handleRetry"
          class="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-black transition-colors"
        >
          Reintentar ({{ retryQueue.length }})
        </button>
        <button
          v-if="swUpdateAvailable"
          @click="applyUpdate"
          class="px-3 py-1.5 rounded-lg bg-white text-brand-700 text-xs font-black transition-colors hover:bg-brand-50"
        >
          Actualizar ahora
        </button>
        <button
          v-if="bannerMessage && !swUpdateAvailable && retryQueue.length > 0"
          @click="dismissBanner"
          class="p-1 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Ocultar aviso"
          title="Ocultar aviso"
        >
          <X class="w-4 h-4" />
        </button>
      </div>
    </div>
  </transition>

  <!-- Frontend desactualizado (el API respondió 426): bloquea hasta actualizar. -->
  <div
    v-if="clientOutdated"
    class="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
    role="alertdialog"
    aria-modal="true"
  >
    <div class="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-6 text-center border border-slate-200 dark:border-slate-800">
      <AlertTriangle class="w-12 h-12 mx-auto text-amber-500" />
      <h2 class="mt-3 text-lg font-black text-slate-900 dark:text-white">Actualización requerida</h2>
      <p class="mt-2 text-sm text-slate-500 dark:text-slate-400">
        Tu aplicación está desactualizada. Actualiza para seguir trabajando con información correcta.
      </p>
      <button
        @click="applyUpdate"
        class="mt-5 w-full py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-black transition-colors"
      >
        Actualizar ahora
      </button>
    </div>
  </div>
</template>
<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { WifiOff, RefreshCw, X, AlertTriangle } from 'lucide-vue-next';
import MainLayout from '@/components/layout/MainLayout.vue';
import { useBranchStore } from '@/stores/branchStore';
import { useConnectivity } from '@/composables/useConnectivity';
import { useToast } from '@/composables/useToast';

const route = useRoute();
const { branchVersion } = useBranchStore();
const { isOnline, retryQueue, flushRetries } = useConnectivity();
const { showSuccess } = useToast();

// Si la ruta tiene meta.layout = 'empty', no usamos layout.
// Si no, usamos MainLayout por defecto.
const layout = computed(() => {
  return route.meta.layout === 'empty' ? 'div' : MainLayout;
});

// --- RX-5: banner de conectividad / actualización ---
const swOfflineDetected = ref(false);
const swUpdateAvailable = ref(false);
const bannerDismissed = ref(false);
// El API exigió otra versión del frontend (426): se bloquea hasta actualizar.
const clientOutdated = ref(false);

const bannerMessage = computed(() => {
  if (!isOnline.value || swOfflineDetected.value) {
    if (retryQueue.value.length > 0) {
      return `Sin conexión: hay ${retryQueue.value.length} operación(es) guardadas para reenviar.`;
    }
    return 'Sin conexión a internet. La aplicación funciona en modo offline.';
  }
  if (retryQueue.value.length > 0 && !bannerDismissed.value) {
    return 'Conexión restablecida: hay operaciones pendientes por reenviar.';
  }
  return '';
});

const handleOfflineEvent = () => { swOfflineDetected.value = true; };
const handleSwUpdated = () => { swUpdateAvailable.value = true; };
const handleClientOutdated = () => { clientOutdated.value = true; };

const handleRetry = async () => {
  const flushed = await flushRetries();
  if (flushed > 0) showSuccess(`${flushed} operación(es) reenviada(s) correctamente.`);
  bannerDismissed.value = true;
};

const dismissBanner = () => { bannerDismissed.value = true; };

// Fuerza al service worker a buscar la nueva versión y recarga para aplicarla.
const applyUpdate = async () => {
  try {
    const reg = await navigator.serviceWorker?.getRegistration?.();
    if (reg) await reg.update();
  } catch (e) {
    // Si falla la actualización del SW, igual recargamos.
  }
  window.location.reload();
};

onMounted(() => {
  window.addEventListener('nexus:sw-updated', handleSwUpdated);
  window.addEventListener('nexus:offline-detected', handleOfflineEvent);
  window.addEventListener('nexus:client-outdated', handleClientOutdated);
  // Por si el 426 llegó antes de registrar el listener.
  if (window.__nexusClientOutdated) clientOutdated.value = true;
});

onBeforeUnmount(() => {
  window.removeEventListener('nexus:sw-updated', handleSwUpdated);
  window.removeEventListener('nexus:offline-detected', handleOfflineEvent);
  window.removeEventListener('nexus:client-outdated', handleClientOutdated);
});
</script>

<style scoped>
.banner-enter-active, .banner-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.banner-enter-from, .banner-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>